import { useId, useMemo, useState } from 'react'
import { CheckIcon, Mtds, SubPurpose } from 'prove-common'

type EditableSubPurposeFieldProps = {
  value?: number
  subPurposes: SubPurpose[]
  canEdit: boolean
  isSaving?: boolean
  onSave: (subPurposeId?: number) => void | Promise<void>
}

export const EditableSubPurposeField = ({
  value,
  subPurposes,
  canEdit,
  isSaving = false,
  onSave
}: EditableSubPurposeFieldProps) => {
  const popoverId = useId()
  const [search, setSearch] = useState('')

  const selected = subPurposes.find((subPurpose) => subPurpose.id === value)

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return subPurposes
    return subPurposes.filter((subPurpose) =>
      subPurpose.name.toLowerCase().includes(query)
    )
  }, [search, subPurposes])

  if (!canEdit) return <>{selected?.name ?? '-'}</>

  const handleSelect = (nextId?: number) => {
    const resolved = nextId === value ? undefined : nextId
    if (resolved !== value) void onSave(resolved)
    setSearch('')
  }

  return (
    <>
      <Mtds.Button
        type='button'
        data-variant='tertiary'
        popoverTarget={popoverId}
        disabled={isSaving}
        className='-mx-1 h-auto min-h-0 justify-start p-1 font-normal'
      >
        {selected ? (
          selected.name
        ) : (
          <span style={{ color: 'var(--mtds-color-text-subtle)' }}>
            Velg underhensikt
          </span>
        )}
      </Mtds.Button>
      <Mtds.Popover id={popoverId} className='w-72 p-2'>
        <div className='grid gap-2'>
          <Mtds.Heading as='p' data-size='2xs'>
            Velg underhensikt
          </Mtds.Heading>
          <Mtds.Field
            as='input'
            type='search'
            aria-label='Søk etter underhensikt'
            placeholder='Søk...'
            value={search}
            onChange={({ target }) => setSearch(target.value)}
          />
          <menu className='grid max-h-72 gap-1 overflow-auto p-0'>
            {filtered.map((subPurpose) => (
              <li key={subPurpose.id}>
                <Mtds.Button
                  type='button'
                  data-variant='tertiary'
                  popoverTargetAction='hide'
                  popoverTarget={popoverId}
                  onClick={() => handleSelect(subPurpose.id)}
                  className='w-full justify-between font-normal'
                >
                  {subPurpose.name}
                  {subPurpose.id === value && <CheckIcon aria-hidden />}
                </Mtds.Button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li>
                <Mtds.Muted className='px-2 py-1'>Ingen treff</Mtds.Muted>
              </li>
            )}
          </menu>
        </div>
      </Mtds.Popover>
    </>
  )
}
